import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { ChevronDown, HelpCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Static FAQ entries. The registration-cost entry is dynamically replaced at
// render-time from /api/program-configs so prices and the early-bird deadline
// always match the admin-edited source of truth. All other entries are static.
const staticFaqData = [
  {
    question: "What age groups can participate?",
    answer: "Park City Chiefs welcomes athletes ages 5–14 across 10 divisions:\n\n**Football:** Little Chiefs (5–7), Tiny Chiefs (7–8), Mighty Chiefs (9), Junior Chiefs (10), Varsity Chiefs (11), Senior Chiefs (12), Elite Chiefs (13)\n\n**Cheerleading:** Cheer Sparks (5–8), Cheer Stars (9–11), Cheer Elite (12–14)\n\nPlayers are placed in the division that matches their age to ensure fair and safe competition."
  },
  {
    question: "When does the season start and end?",
    answer: "The Fall 2026 season runs from **September 1 through December 15**. Summer conditioning and practice begins in **July**, with practices running Monday through Friday from 5:45–7:45 PM through August. Once school starts, practices shift to three days a week from 5:30–7:30 PM."
  },
  {
    question: "How often are practices and games?",
    answer: "**Summer (July–August):** Monday through Friday, 5:45–7:45 PM\n\n**During School (September–December):** Three days per week, 5:30–7:30 PM. The specific practice days are determined by each Head Coach.\n\n**Games** are typically held on weekends. The full game schedule is posted in the app under the Schedule tab once available."
  },
  {
    question: "Where are practices and games held?",
    answer: "**Practices** are held at **Luis Marin School — Freedom Field**.\n\n**Game locations** are determined by Parks and Recreation and will be announced as the schedule is finalized. Check the Schedule tab in the app for the latest game locations."
  },
  {
    question: "What equipment is required?",
    answer: "**Provided by Park City Chiefs:** Helmet, shoulder pads, uniform, and Guardian Caps.\n\n**Provided by parent/guardian:** Football cleats, mouthguard, and practice pants.\n\nAll equipment provided by the organization must be returned at the end of the season."
  },
  {
    question: "How much does registration cost?",
    answer: "Registration fees by program:\n\n• **Flag Football:** $200 (Early Bird / Sibling: $150)\n• **Tackle Football:** $300 (Early Bird / Sibling: $225)\n• **Cheerleading:** $400 (Early Bird / Sibling: $325)\n\nThe **Early Bird deadline** is May 10, 2026. Sibling discounts match the early bird pricing. Payment plans are available through the app.",
    _dynamicKey: 'registration_cost'
  },
  {
    question: "Is football safe for my child?",
    answer: "Player safety is our top priority. Park City Chiefs takes multiple steps to ensure a safe environment:\n\n• All coaches are required to complete **USA Football certification**, **CPR/First Aid training**, and **Concussion Training** before coaching\n• All coaches undergo **background checks**\n• **Guardian Caps** are provided for all players to add an extra layer of head protection during practices and games\n• We follow age-appropriate contact guidelines and use proper tackling techniques as taught by USA Football's Heads Up program\n• Our **Student Accountability Program** works with teachers to monitor academic performance and behavior alongside athletics"
  },
  {
    question: "Who are the coaches?",
    answer: "Our coaching staff is made up of dedicated volunteers from the community who are passionate about youth development. Every coach must complete:\n\n• **Background check** (verified before any player contact)\n• **USA Football coaching certification**\n• **CPR/First Aid certification**\n• **Concussion awareness training**\n\nCoach profiles and their certification status can be viewed through the app. Interested in coaching? Apply through the \"Become a Coach\" button in the app."
  },
  {
    question: "Can beginners join the team?",
    answer: "Absolutely! Park City Chiefs welcomes players of **all skill levels**, including those who have never played football or cheered before. Our younger divisions (Little Chiefs and Tiny Chiefs) focus heavily on fundamentals, teamwork, and having fun. Our coaches are trained to develop athletes at every level."
  },
  {
    question: "How are teams formed?",
    answer: "Teams are organized by **age-based divisions** to ensure safe and fair play. Players are placed into their appropriate division based on their age at the time of registration. Within each division, roster assignments are managed by the coaching staff to balance skill levels and ensure every player gets meaningful playing time."
  },
  {
    question: "What is the expected time commitment?",
    answer: "Parents and players are expected to attend **all Park City Chiefs activities**:\n\n• **Practices are mandatory** — Summer: 5 days/week (2 hours each), School year: 3 days/week (2 hours each)\n• **Games** on weekends (arrive 1.5 hours early)\n• **Fundraisers and team events** throughout the season\n\nWe understand life happens — communicate with your Head Coach if you have a conflict."
  },
  {
    question: "Do I really need to arrive 1.5 hours before a game?",
    answer: "**Yes.** Arriving 1.5 hours before game time is the standard protocol for all Park City Chiefs teams. This time is used for:\n\n• Equipment check and gear-up\n• Team warm-ups and stretching\n• Pre-game review and strategy\n• Coach and player preparation\n\nThis is a league-wide expectation and helps ensure our athletes are properly prepared and safe before taking the field."
  },
  {
    question: "How can parents get involved?",
    answer: "All parents have the opportunity to volunteer! Opportunities include game day setup, snack stand, chain crew, post-game cleanup, fundraiser support, and more. Check the **Volunteer** tab in the app to browse open opportunities and sign up. Parent involvement is what makes Park City Chiefs a true community."
  },
  {
    question: "What happens in bad weather?",
    answer: "• **Practices:** Cancellations are sent out **1 hour before** the scheduled practice time via the app. If practice is canceled, an **indoor backup** location will be provided when available.\n• **Games:** Game cancellations or rescheduling decisions are made by the league (Parks and Recreation). Updates will be posted in the app as soon as they are confirmed.\n\nAlways check the app for the latest updates on weather-related changes."
  },
  {
    question: "Are there opportunities for fundraising?",
    answer: "Yes! Park City Chiefs runs fundraising campaigns throughout the season to support equipment, field maintenance, and program costs. Check the **Fundraising** tab in the app for active campaigns and ways to contribute. Past events include popcorn sales, community events, and sponsor drives."
  },
  {
    question: "How do I register my child?",
    answer: "Registration is done through the Park City Chiefs app:\n\n1. Log in or create your family account\n2. Go to **My Family** and add your player(s)\n3. Complete the registration form including medical info and emergency contacts\n4. Upload any required documents\n5. Submit payment (payment plans available)\n\nNeed help? Reach out to the admin team through the Messages tab."
  },
  {
    question: "Where can I purchase football cleats, mouthguards, and other gear?",
    answer: "Parent-provided gear (cleats, mouthguards, and practice pants) can be purchased at:\n\n• **Amazon** — amazon.com\n• **Dick's Sporting Goods** — dickssportinggoods.com\n• **Nike** — nike.com\n• **Local sporting goods stores**\n\n**Tip:** Make sure cleats are football-specific (not soccer or baseball cleats). A properly fitted mouthguard is essential — boil-and-bite models work great for youth players."
  }
];

const FAQItem = ({ question, answer, isOpen, onToggle, index }) => (
  <div 
    className={`border-b border-gray-100 last:border-0 ${isOpen ? 'bg-navy/[0.02]' : ''}`}
    data-testid={`faq-item-${index}`}
  >
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between py-4 px-4 text-left hover:bg-gray-50/50 transition-colors group"
      data-testid={`faq-toggle-${index}`}
    >
      <span className={`text-sm font-medium pr-4 ${isOpen ? 'text-navy' : 'text-gray-800'}`}>
        {question}
      </span>
      <ChevronDown className={`w-4 h-4 flex-shrink-0 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180 text-navy' : ''}`} />
    </button>
    {isOpen && (
      <div className="px-4 pb-4 text-sm text-gray-600 leading-relaxed whitespace-pre-line">
        {answer.split(/(\*\*[^*]+\*\*)/).map((part, i) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={i} className="text-gray-800">{part.slice(2, -2)}</strong>;
          }
          return <span key={i}>{part}</span>;
        })}
      </div>
    )}
  </div>
);

const ParentFAQ = ({ autoOpen = false }) => {
  const [openItems, setOpenItems] = useState({});
  const [showFAQ, setShowFAQ] = useState(autoOpen);
  const [programConfigs, setProgramConfigs] = useState(null);
  const faqRef = useRef(null);

  useEffect(() => {
    if (autoOpen && faqRef.current) {
      setTimeout(() => {
        faqRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, [autoOpen]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/program-configs`, { withCredentials: true });
        if (Array.isArray(r.data) && r.data.length) setProgramConfigs(r.data);
      } catch (e) {
        // Graceful fallback — keep static answer.
      }
    })();
  }, []);

  // Build the live cost-answer string from program_configs. Falls back to the
  // static answer when configs are unavailable.
  const buildCostAnswer = () => {
    if (!programConfigs) return null;
    const labelByKey = {
      flag_football: 'Flag Football',
      tackle_football: 'Tackle Football',
      cheer: 'Cheerleading',
    };
    const order = ['flag_football', 'tackle_football', 'cheer'];
    const lines = order
      .map(k => programConfigs.find(c => c.program_key === k))
      .filter(c => c && c.active !== false)
      .map(c => `• **${labelByKey[c.program_key] || c.name}:** $${Number(c.standard_price).toFixed(0)} (Early Bird / Sibling: $${Number(c.early_bird_price).toFixed(0)})`);
    if (!lines.length) return null;
    const firstDeadline = programConfigs.find(c => c.early_bird_deadline)?.early_bird_deadline;
    let deadlineText = 'May 10, 2026';
    if (firstDeadline) {
      const [y, m, d] = String(firstDeadline).split('-').map(Number);
      if (y && m && d) {
        deadlineText = new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      }
    }
    return `Registration fees by program:\n\n${lines.join('\n')}\n\nThe **Early Bird deadline** is ${deadlineText}. Sibling discounts match the early bird pricing. Payment plans are available through the app.`;
  };

  const faqData = staticFaqData.map(item => {
    if (item._dynamicKey === 'registration_cost') {
      const live = buildCostAnswer();
      if (live) return { ...item, answer: live };
    }
    return item;
  });

  const toggleItem = (index) => {
    setOpenItems(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const toggleAll = () => {
    if (Object.values(openItems).some(v => v)) {
      setOpenItems({});
    } else {
      const all = {};
      faqData.forEach((_, i) => { all[i] = true; });
      setOpenItems(all);
    }
  };

  if (!showFAQ) {
    return (
      <button
        onClick={() => setShowFAQ(true)}
        className="w-full flex items-center justify-center gap-2 py-3 text-sm text-gray-400 hover:text-navy transition-colors mt-6"
        data-testid="faq-show-btn"
      >
        <HelpCircle className="w-4 h-4" />
        <span>Have questions? View Parent FAQ</span>
      </button>
    );
  }

  return (
    <div className="mt-6" data-testid="parent-faq" ref={faqRef}>
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50/80 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <HelpCircle className="w-4 h-4 text-navy" />
            <h3 className="text-sm font-bold text-navy uppercase tracking-wider">Parent FAQ</h3>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={toggleAll}
              className="text-xs text-gray-500 hover:text-navy transition-colors"
              data-testid="faq-toggle-all"
            >
              {Object.values(openItems).some(v => v) ? 'Collapse All' : 'Expand All'}
            </button>
            <button
              onClick={() => { setShowFAQ(false); setOpenItems({}); }}
              className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
              data-testid="faq-hide-btn"
            >
              Hide
            </button>
          </div>
        </div>
        <div>
          {faqData.map((item, index) => (
            <FAQItem
              key={index}
              index={index}
              question={item.question}
              answer={item.answer}
              isOpen={openItems[index] || false}
              onToggle={() => toggleItem(index)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ParentFAQ;
